import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

import Icon from '../../components/AppIcon';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import FAQSection from './components/FAQSection';
import GuideCard from './components/GuideCard';
import VideoTutorial from './components/VideoTutorial';
import ContactSupport from './components/ContactSupport';
import Footer from '../homepage-sales-landing/components/Footer';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [bookmarkedArticles, setBookmarkedArticles] = useState([]);

  const categories = [
    { id: 'all', label: 'All Topics', icon: 'Grid' },
    { id: 'getting-started', label: 'Getting Started', icon: 'Rocket' },
    { id: 'ai-agents', label: 'AI Agents', icon: 'Bot' },
    { id: 'subscription', label: 'Subscription Management', icon: 'CreditCard' },
    { id: 'publishing', label: 'Publishing', icon: 'BookOpen' },
    { id: 'technical', label: 'Technical Support', icon: 'Settings' }
  ];

  const faqs = {
    'getting-started': [
      {
        id: 'gs-1',
        question: 'How do I get started with AuthorPreneur AI?',
        answer: 'Getting started is easy! First, create an account and choose your subscription tier. Then, access the AI Agent Workflow Hub from your dashboard. Start by defining your book topic, and our 11 AI agents will guide you through each step of the manuscript creation process.',
        relatedArticles: ['Understanding the 11 AI Agents', 'Choosing Your Subscription Tier']
      },
      {
        id: 'gs-2',
        question: 'What are the 11 AI agents and what do they do?',
        answer: 'Our platform features 11 specialized AI agents that work together to create your book: Topic Definition Agent, Market Research Agent, Audience Targeting Agent, Outline Creation Agent, Chapter Writing Agent, Content Enhancement Agent, Cover Design Agent, Formatting Agent, SEO Optimization Agent, KDP Publishing Agent, and Marketing Strategy Agent. Each agent specializes in a specific aspect of book creation.',
        relatedArticles: ['AI Agent Workflow Guide', 'How AI Agents Work Together']
      },
      {
        id: 'gs-3',
        question: 'How long does it take to create a book?',
        answer: 'The timeline varies based on your involvement and book complexity. On average, users complete their first draft within 2-4 weeks using our AI agents. Premium subscribers with priority processing can complete projects faster. The AI agents work continuously, but quality review and personal touches from you ensure the best final product.',
        relatedArticles: ['Project Timeline Guide', 'Optimizing Your Workflow']
      }
    ],
    'ai-agents': [
      {
        id: 'ai-1',
        question: 'Can I customize the AI agent suggestions?',
        answer: 'Absolutely! All AI agent outputs are fully customizable. You can edit, refine, or completely rewrite any suggestions. Think of our AI agents as your collaborative writing assistants - they provide the foundation, and you add your unique expertise and voice.',
        relatedArticles: ['Customizing AI Outputs', 'Best Practices for AI Collaboration']
      },
      {
        id: 'ai-2',
        question: 'What happens if I disagree with an AI agent\'s suggestion?',
        answer: 'You have complete control! You can reject suggestions, request alternatives, or provide specific guidance to the AI agents. Each agent learns from your feedback to provide more aligned suggestions in future interactions. Your expertise guides the AI, not the other way around.',
        relatedArticles: ['Training Your AI Agents', 'Feedback Mechanisms']
      },
      {
        id: 'ai-3',
        question: 'Are the AI-generated manuscripts plagiarism-free?',
        answer: 'Yes, all content generated by our AI agents is original and unique to your project. We use advanced algorithms to ensure originality, and you maintain full ownership of all created content. We recommend running your final manuscript through plagiarism checkers as a best practice before publishing.',
        relatedArticles: ['Content Originality Guarantee', 'Copyright and Ownership']
      }
    ],
    'subscription': [
      {
        id: 'sub-1',
        question: 'What subscription tiers are available?',
        answer: 'We offer three tiers: Basic ($49/month) - 1 active project, standard AI processing; Professional ($99/month) - 3 active projects, priority AI processing, advanced analytics; Enterprise ($199/month) - unlimited projects, fastest AI processing, Skool community access, 1-on-1 consultations, and priority support.',
        relatedArticles: ['Comparing Subscription Tiers', 'Choosing the Right Plan']
      },
      {
        id: 'sub-2',
        question: 'Can I upgrade or downgrade my subscription?',
        answer: 'Yes, you can change your subscription tier at any time from the Subscription Management page. Upgrades take effect immediately, while downgrades apply at the start of your next billing cycle. Your existing projects remain accessible during tier changes.',
        relatedArticles: ['Managing Your Subscription', 'Billing FAQ']
      },
      {
        id: 'sub-3',
        question: 'What happens to my projects if I cancel?',
        answer: 'If you cancel, you\'ll retain access until the end of your current billing period. After cancellation, you can export all your manuscripts, outlines, and research materials. Your projects are preserved in read-only mode for 90 days, allowing you to reactivate and continue if you return.',
        relatedArticles: ['Cancellation Policy', 'Exporting Your Content']
      }
    ],
    'publishing': [
      {
        id: 'pub-1',
        question: 'How does KDP publishing integration work?',
        answer: 'Our KDP Publishing Agent guides you through the entire Amazon KDP process. It formats your manuscript to KDP standards, generates required metadata, creates your book description with SEO optimization, and provides step-by-step instructions for uploading to KDP. Note: You\'ll need your own KDP account to publish.',
        relatedArticles: ['KDP Publishing Guide', 'Preparing for Amazon Upload']
      },
      {
        id: 'pub-2',
        question: 'Can I publish to platforms other than Amazon KDP?',
        answer: 'Yes! While our primary integration is with Amazon KDP, our formatting agent produces industry-standard files compatible with other platforms like IngramSpark, Draft2Digital, and more. You can export your properly formatted manuscript and publish anywhere.',
        relatedArticles: ['Multi-Platform Publishing', 'Export Formats Guide']
      },
      {
        id: 'pub-3',
        question: 'Do you provide ISBN numbers?',
        answer: 'ISBN procurement is not included in our service. You\'ll need to obtain ISBNs separately through Bowker (for US authors) or your country\'s ISBN agency. For Amazon KDP, you can use their free ASIN instead of an ISBN, though we recommend ISBNs for wider distribution.',
        relatedArticles: ['ISBN vs ASIN', 'Publishing Requirements Checklist']
      }
    ],
    'technical': [
      {
        id: 'tech-1',
        question: 'What browsers are supported?',
        answer: 'AuthorPreneur AI works best on the latest versions of Chrome, Firefox, Safari, and Edge. We recommend using Chrome or Firefox for optimal performance. Ensure JavaScript is enabled and your browser is up to date.',
        relatedArticles: ['System Requirements', 'Browser Compatibility']
      },
      {
        id: 'tech-2',
        question: 'My manuscript isn\'t saving. What should I do?',
        answer: 'First, check your internet connection. The platform auto-saves every 30 seconds when online. If issues persist: 1) Clear your browser cache, 2) Try a different browser, 3) Export your current work as backup, 4) Contact support with your project ID and error details.',
        relatedArticles: ['Troubleshooting Save Issues', 'Auto-Save Features']
      },
      {
        id: 'tech-3',
        question: 'How do I report a bug or technical issue?',
        answer: 'Report bugs through: 1) Live chat (fastest response), 2) Support ticket via the Help Center, 3) Email support@authorpreneurag.com. Include: your account email, detailed issue description, browser/device info, and screenshots if applicable. Premium subscribers receive priority technical support.',
        relatedArticles: ['Reporting Technical Issues', 'Support Response Times']
      }
    ]
  };

  const guides = [
    {
      id: 'guide-1',
      title: 'Complete Manuscript Creation Workflow',
      description: 'Step-by-step walkthrough of creating your first book from topic selection to final manuscript',
      duration: '45 min',
      steps: 11,
      difficulty: 'Beginner',
      category: 'getting-started',
      icon: 'BookOpen'
    },
    {
      id: 'guide-2',
      title: 'Mastering the Cover Design Agent',
      description: 'Learn how to create professional book covers that sell using our AI-powered design agent',
      duration: '20 min',
      steps: 6,
      difficulty: 'Intermediate',
      category: 'ai-agents',
      icon: 'Image'
    },
    {
      id: 'guide-3',
      title: 'Publishing to Amazon KDP',
      description: 'Complete guide to formatting, uploading, and optimizing your book for Amazon Kindle Direct Publishing',
      duration: '30 min',
      steps: 8,
      difficulty: 'Intermediate',
      category: 'publishing',
      icon: 'Upload'
    },
    {
      id: 'guide-4',
      title: 'Optimizing AI Agent Performance',
      description: 'Advanced techniques for getting the best results from all 11 AI agents in your workflow',
      duration: '25 min',
      steps: 7,
      difficulty: 'Advanced',
      category: 'ai-agents',
      icon: 'Zap'
    }
  ];

  const videoTutorials = [
    {
      id: 'video-1',
      title: 'Platform Overview: Your First 15 Minutes',
      description: 'Quick introduction to the AuthorPreneur AI platform and key features',
      duration: '15:30',
      thumbnail: 'https://placehold.co/400x225/6366f1/ffffff?text=Platform+Overview',
      category: 'getting-started',
      views: '12.5K'
    },
    {
      id: 'video-2',
      title: 'Working with the Chapter Writing Agent',
      description: 'In-depth tutorial on using the Chapter Writing Agent for high-quality content',
      duration: '22:45',
      thumbnail: 'https://placehold.co/400x225/8b5cf6/ffffff?text=Chapter+Writing',
      category: 'ai-agents',
      views: '8.3K'
    },
    {
      id: 'video-3',
      title: 'From Draft to Published: Complete Process',
      description: 'End-to-end walkthrough of taking your manuscript from first draft to live on Amazon',
      duration: '35:20',
      thumbnail: 'https://placehold.co/400x225/ec4899/ffffff?text=Publishing+Process',
      category: 'publishing',
      views: '15.2K'
    }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? Object.values(faqs)?.flat()
    : faqs?.[selectedCategory] || [];

  const filteredGuides = selectedCategory === 'all'
    ? guides
    : guides?.filter(guide => guide?.category === selectedCategory);

  const handleBookmark = (articleId) => {
    setBookmarkedArticles(prev => 
      prev?.includes(articleId) 
        ? prev?.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Help Center - AuthorPreneur AI Support & Resources</title>
        <meta 
          name="description" 
          content="Comprehensive self-service support for AuthorPreneur AI. Find FAQs, step-by-step guides, video tutorials, and troubleshooting assistance for our AI-powered book creation platform." 
        />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-b border-border">
        <div className="container mx-auto px-4 py-12 lg:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6">
              <Icon name="HelpCircle" size={32} color="var(--color-primary)" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              How can we help you?
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Search our knowledge base or browse categories to find answers to your questions
            </p>

            <SearchBar 
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search for help articles, guides, or topics..."
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Category Filter */}
        <CategoryFilter 
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name="FileText" size={24} color="var(--color-primary)" />
              <span className="text-2xl font-bold text-foreground">250+</span>
            </div>
            <p className="text-sm text-muted-foreground">Help Articles</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Video" size={24} color="var(--color-primary)" />
              <span className="text-2xl font-bold text-foreground">45+</span>
            </div>
            <p className="text-sm text-muted-foreground">Video Tutorials</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Users" size={24} color="var(--color-primary)" />
              <span className="text-2xl font-bold text-foreground">50K+</span>
            </div>
            <p className="text-sm text-muted-foreground">Community Members</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Clock" size={24} color="var(--color-primary)" />
              <span className="text-2xl font-bold text-foreground">&lt; 2h</span>
            </div>
            <p className="text-sm text-muted-foreground">Avg Response Time</p>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-2">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Quick answers to common questions about AuthorPreneur AI
              </p>
            </div>
          </div>

          <FAQSection 
            faqs={filteredFAQs}
            bookmarkedArticles={bookmarkedArticles}
            onBookmark={handleBookmark}
          />
        </section>

        {/* Interactive Guides */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-2">
                Step-by-Step Guides
              </h2>
              <p className="text-muted-foreground">
                Detailed walkthroughs for complex processes with progress tracking
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {filteredGuides?.map((guide) => (
              <GuideCard 
                key={guide?.id}
                guide={guide}
              />
            ))}
          </div>
        </section>

        {/* Video Tutorials */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-2">
                Video Tutorials
              </h2>
              <p className="text-muted-foreground">
                Watch and learn with our comprehensive video library
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {videoTutorials?.map((video) => (
              <VideoTutorial 
                key={video?.id}
                video={video}
              />
            ))}
          </div>
        </section>

        {/* Contact Support */}
        <section>
          <ContactSupport />
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default HelpCenter;